import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import { ButtonVariants } from "../components/common/Button/constants";

const DEFAULT_CONFIRM_TEXT = "Confirm";
const DEFAULT_CANCEL_TEXT = "Cancel";

export enum SoundType {
  GOOD,
  BAD,
}

export interface QueuedSound {
  id: string;
  soundType: SoundType;
}

export enum AlertState {
  OFF,
  ALERTING,
}

export enum ConfirmState {
  OFF,
  WAITING_FOR_CONFIRMATION,
  CONFIRMED,
}

export enum OptionsState {
  OFF,
  WAITING_FOR_SELECTION,
  SELECTED,
}

interface OptionItem {
  text: string;
  variant?: ButtonVariants;
}

export interface Alerts {
  alert: AlertState;
  confirm: ConfirmState;
  confirmText: string;
  cancelText: string;
  alertMessage: string | null;
  confirmMessage: string | null;
  soundsToPlay: QueuedSound[];
  options: OptionsState;
  optionsMessage: string | null;
  optionItems: OptionItem[];
  selectedOptionIndex: number | null;
}

export const authSlice = createSlice({
  name: "auth",
  initialState: {
    alert: AlertState.OFF,
    confirm: ConfirmState.OFF,
    alertMessage: null,
    confirmMessage: null,
    confirmText: DEFAULT_CONFIRM_TEXT,
    cancelText: DEFAULT_CANCEL_TEXT,
    soundsToPlay: [],
    options: OptionsState.OFF,
    optionsMessage: null,
    optionItems: [],
    selectedOptionIndex: null,
  } as Alerts,
  reducers: {
    setConfirm: (
      state,
      action: PayloadAction<{
        confirm: ConfirmState;
        message: string | null;
        confirmText?: string;
        cancelText?: string;
      }>,
    ) => {
      state.confirm = action.payload.confirm;
      state.confirmMessage = action.payload.message;
      state.confirmText = action.payload.confirmText || DEFAULT_CONFIRM_TEXT;
      state.cancelText = action.payload.cancelText || DEFAULT_CANCEL_TEXT;
    },
    setOptions: (
      state,
      action: PayloadAction<{
        options: OptionsState;
        optionsMessage: string | null;
        optionItems: OptionItem[];
      }>,
    ) => {
      state.options = action.payload.options;
      state.optionsMessage = action.payload.optionsMessage;
      state.optionItems = action.payload.optionItems;
      state.selectedOptionIndex = null;
    },
    selectOption: (
      state,
      action: PayloadAction<{
        index: number;
      }>,
    ) => {
      state.options = OptionsState.SELECTED;
      state.selectedOptionIndex = action.payload.index;
    },
    setAlert: (
      state,
      action: PayloadAction<{
        alert: AlertState;
        message: string | null;
      }>,
    ) => {
      state.alert = action.payload.alert;
      state.alertMessage = action.payload.message;
    },
    addSound: (
      state,
      action: PayloadAction<{
        soundType: SoundType;
      }>,
    ) => {
      state.soundsToPlay.push({
        id: uuidv4(),
        soundType: action.payload.soundType,
      });
    },
    removeSounds: (
      state,
      action: PayloadAction<{
        idsToRemove: string[];
      }>,
    ) => {
      state.soundsToPlay = state.soundsToPlay.filter((sound) => {
        return !action.payload.idsToRemove.includes(sound.id);
      });
    },
  },
});

export const {
  setConfirm,
  setOptions,
  selectOption,
  setAlert,
  addSound,
  removeSounds,
} = authSlice.actions;

export default authSlice.reducer;
